import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Grid, Skeleton, TextField } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import useBulkAssessmentStore, { getBulkAssessmentStore } from './useBulkAssessmentStore';
import BulkExcelUpload from './components/BulkExcelUpload';
import { getCurrentTime } from '../../utils/getCurrentTime';
import { send_component_save_request, send_request } from '../../utils/Request';
import { injectMetadataFields } from '../../utils/buildComponentSavePayload';
import BulkItemKey from './components/BulkItemKey';
import checkKey from '../../utils/CheckKey';
import useProjectStore from '../ProjectCanvas/useProjectStore';
import checkKeyUtil from '../../utils/CheckKeyUtil';
import replacePlaceholderValue from '../../utils/ReplacePlaceholderValue';
import config from '../../config';
import { IndividualComponentLoader } from '../../utils/ComponentLoadingSkeleton';
import W86PlaceholderTextField from '../W86Components/W86PlaceholderTextField';
import CustomPaper from '../common/CustomPaper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import AddIcon from '@mui/icons-material/Add';

const BulkAssessmentTemplate = (props) => {
  const [focus, setFocus] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(false);
  const [checkKeyState, setCheckKeyState] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [keyErrors, setKeyErrors] = useState([]);
  const [spreadsheetOption, setSpreadsheetOption] = useState(null);
  const [loading, setLoading] = useState(true);
  const [description, setDescription] = useState(null);
  const [status, setStatus] = useState(null);

  const store = useMemo(() => getBulkAssessmentStore(props.component_id), [props.component_id]);

  useEffect(() => {
    const loadData = async () => {
      const { component_id } = props;
      if (component_id) {
        if (store.getState().loaded) {
          props.onComponentNameChanged(store.getState().name);
          props.onLastModifiedChanged(store.getState().lastModified);
          setLoading(false);
          props.setChildTemplateLoaded(true);
          return;
        }

        const url = `project-service/project/component/${component_id}`;
        const json = await send_request(url, '', {}).catch(err => {
          throw Error(err.message);
        });

        if (json && json.data) {
          store.getState().setData(json.data);
          store.setState({ loaded: true });
          props.onComponentDescriptionChanged(json.data.description);
          props.onComponentNameChanged(store.getState().name);
          props.onLastModifiedChanged(store.getState().lastModified);
          setSpreadsheetOptionFromData(json.data);
        }

        setLoading(false);
        props.setChildTemplateLoaded(true);
      }
    };
    loadData();
  }, []);

  const setSpreadsheetOptionFromData = useCallback((data) => {
    try {
      let option = data.componentData.data.option;
      if (!option) option = 'manually_enter_columns';
      setSpreadsheetOption(option);
    } catch (e) {
      setSpreadsheetOption('upload_sample_spreadsheet');
    }
  }, []);

  const handingExcel = useCallback((data) => {
    store.getState().addDataFromExcel(data);
    setOpen(false);
  }, []);

  const handleKeyChange = useCallback((e) => {
    const placeholderValue = replacePlaceholderValue(e.target.value);
    store.getState().setContent(placeholderValue, 'key');

    const { data } = store.getState();
    const value = checkKey(placeholderValue, data);

    setCheckKeyState(value[0]);
    setErrorMessage(value[1]);
  }, []);

  const setKeyErrorsFn = useCallback((keyStatus, key) => {
    setKeyErrors(prev => {
      if (keyStatus) {
        if (!prev.includes(key)) return [...prev, key];
        return prev;
      } else {
        return prev.filter(x => x !== key);
      }
    });
  }, []);

  const data = store((s) => s.data);
  const { key, fields } = data;
  const { isExpanded } = props;

  if (!loading) {
    return (
      <CustomPaper style={{ width: '100%' }}>
        <Grid container alignItems={'center'}>
          <Grid item marginRight={2}>
            Placeholder
          </Grid>
          <Grid item>
            <W86PlaceholderTextField
              value={key}
              error={checkKeyState ? true : false}
              helperText={checkKeyState ? errorMessage : null}
              placeholderText="Enter placeholder for calculator"
              handleChange={handleKeyChange}
            />
          </Grid>
        </Grid>
        <Grid container spacing={1} style={{ marginTop: '16px', marginBottom: '24px' }}>
          <Grid item xs={12}>
            <span className="infoText">Link to the upload spreadsheet portal</span>
          </Grid>
          <Grid
            container
            item
            direction="column"
            className="componentContents"
            {...(props.isExpanded && {
              xs: 12,
              sm: 10,
              md: 10,
              lg: 7,
              xl: 7,
            })}
          >
            <TextField
              value={`${config.FORM.FORM_FRONT_END}/bulk/` + props.component_id}
              className="key_padding "
              variant="outlined"
              disabled={true}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <CopyToClipboard
                      text={`${config.FORM.FORM_FRONT_END}/bulk/` + props.component_id}
                    >
                      <FileCopyIcon className="custom-icon-btn" />
                    </CopyToClipboard>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            Map spreadsheet columns
          </Grid>
          <Grid item xs={12}>
            <Grid container>
              <Grid
                item
                xs={isExpanded && 4}
                marginRight={spreadsheetOption === 'upload_sample_spreadsheet' ? 2 : 0}
              >
                {spreadsheetOption ? (
                  <RadioGroup
                    name="spreadsheetOption"
                    value={spreadsheetOption}
                    onChange={e => setSpreadsheetOption(e.target.value)}
                  >
                    <FormControlLabel
                      value="upload_sample_spreadsheet"
                      control={<Radio />}
                      label="Upload sample spreadsheet"
                    />
                    <FormControlLabel
                      value="manually_enter_columns"
                      control={<Radio />}
                      label="Manually enter columns"
                    />
                  </RadioGroup>
                ) : (
                  <Skeleton
                    height={35}
                    width={400}
                    variant={'rectangular'}
                    style={{ borderRadius: '4px', background: '#E8E8E8' }}
                  />
                )}
              </Grid>
              {spreadsheetOption === 'upload_sample_spreadsheet' && (
                <Grid item>
                  <Button variant="contained" onClick={() => setOpen(!open)}>
                    Upload
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <span className="infoText">
              {spreadsheetOption === 'upload_sample_spreadsheet'
                ? 'Upload a sample spreadsheet to automatically map the columns'
                : 'Manually enter each column in the the spreadsheet to be uploaded'}
            </span>
          </Grid>
          {spreadsheetOption === 'manually_enter_columns' ? (
            <Grid item xs={12} className="addColumnButtonWrapper">
              <Button
                variant="outlined"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => store.getState().addComponentKeyData()}
                className="addColumnButton"
              >
                Add column
              </Button>
            </Grid>
          ) : (
            <Grid item xs={12} />
          )}
        </Grid>
        <Grid spacing={1} direction={'column'} container>
          <Grid container item direction={'row'} xs={12} fontSize={'14px'}>
            <Grid item xs={8}>
              Column name{' '}
            </Grid>
            <Grid item xs={3}>
              Data type
            </Grid>
            <Grid item xs={1}>
              {' '}
            </Grid>
          </Grid>
          {fields.map((component, index) => (
            <Grid item container xs={12} key={component.id}>
              <BulkItemKey key={component.id} component={component} index={index} />
            </Grid>
          ))}
        </Grid>

        <BulkExcelUpload
          open={open}
          handleCancel={() => setOpen(false)}
          handingExcel={handingExcel}
        />
      </CustomPaper>
    );
  }
  return <IndividualComponentLoader />;
};

export default BulkAssessmentTemplate;
